import { useMemo } from "react"
import { useMap } from "react-leaflet"

import VesselIcon from "./VesselIcon"
import {
  buildVesselsGeojson,
  buildLastPositionVessels,
  getNearestFutureTick,
} from "@/utils/vesselHelper"

export default function VesselController({
  vesselReportedRoutes,
  vesselPlanRoutes,
  vesselLabelMap,
  markedVesselNames,
  lastPositions,
  switchReported,
  switchPlan,
  recenterToPoint,
  setMarkedVesselNames,
  setOpenDrawer,
  setOpenVesselList,
  setSelectedVessel,
  setSelectedVesselAnchor,
  resized,
  sliderTicks,
  sliderPosition,
  setVoyageActive,
  switchVoyagePlannerActive,
  setOpenRouteTable,
  onMapVesselClick,
}) {
  const map = useMap()
  const layers = []

  // Memoized vessel snapshots for each route type
  const reportedVessels = useMemo(() => {
    if (!switchReported || !sliderPosition || !vesselReportedRoutes) return []
    return buildVesselsGeojson({ routes: vesselReportedRoutes, sliderPosition })
  }, [switchReported, vesselReportedRoutes, sliderPosition])

  const planVessels = useMemo(() => {
    if (!switchPlan || !sliderPosition || !vesselPlanRoutes) return []
    return buildVesselsGeojson({
      routes: vesselPlanRoutes,
      sliderPosition,
      labels: vesselLabelMap,
    })
  }, [switchPlan, vesselPlanRoutes, sliderPosition, vesselLabelMap])

  const autoVessels = useMemo(() => {
    if (switchReported) return []

    if (!sliderTicks || sliderTicks.length === 0) return []

    const lastTick = sliderTicks[sliderTicks.length - 1]
    const nearestFutureTick = getNearestFutureTick(sliderTicks, lastTick)

    // When a plan/suggested/archived route is shown, keep the marked vessel's
    // last reported position visible alongside the plan marker. Otherwise show
    // the last reported position for all reported routes.
    const hasPlanVesselsToShow = switchPlan && (planVessels?.length ?? 0) > 0
    const markedNames = new Set((markedVesselNames ?? []).map((m) => m.vesselName))

    const reportedRoutesToShow = hasPlanVesselsToShow
      ? (vesselReportedRoutes ?? []).filter((r) => markedNames.has(r.points.vesselName))
      : (vesselReportedRoutes ?? [])

    // 1) Last reported position at lastTick
    let vessels = buildVesselsGeojson({
      routes: reportedRoutesToShow,
      sliderPosition: lastTick,
    })

    // 2) Fallback: marked ships without reported route -> use plan
    //    (skipped when plan vessels already render their own marker)
    if (!hasPlanVesselsToShow) {
      const reportedNames = new Set((vesselReportedRoutes ?? []).map((r) => r.points.vesselName))

      const planFallbackRoutes = (vesselPlanRoutes ?? []).filter(
        (r) => markedNames.has(r.points.vesselName) && !reportedNames.has(r.points.vesselName)
      )

      if (planFallbackRoutes.length > 0) {
        const planVessels = buildVesselsGeojson({
          routes: planFallbackRoutes,
          sliderPosition: nearestFutureTick || lastTick,
          labels: vesselLabelMap,
        })
        vessels = vessels.concat(planVessels)
      }
    }

    return vessels
  }, [
    switchReported,
    switchPlan,
    planVessels,
    sliderTicks,
    vesselReportedRoutes,
    vesselPlanRoutes,
    vesselLabelMap,
    markedVesselNames,
  ])

  // Latest-position markers: all vessels when none is selected, otherwise only
  // the selected one.
  const lastPositionVessels = useMemo(() => {
    const drawnNames = new Set()
    for (const v of [...reportedVessels, ...autoVessels]) {
      const name = v?.points?.vesselName
      if (name != null) drawnNames.add(name)
    }

    const selectedNames = new Set(
      (markedVesselNames ?? [])
        .map((m) => m?.vesselName)
        .filter((n) => n != null),
    )

    const visible = (lastPositions ?? []).filter((p) => {
      if (drawnNames.has(p?.vesselName)) return false
      if (selectedNames.size > 0 && !selectedNames.has(p?.vesselName))
        return false
      return true
    })
    return buildLastPositionVessels(visible)
  }, [lastPositions, reportedVessels, autoVessels, markedVesselNames])

  const addVesselIcon = (vessels, keySuffix) => {
    layers.push(
      <VesselIcon
        key={`VesselIcon-${keySuffix}-${sliderPosition?.label ?? "na"}-${vessels?.length ?? 0}`}
        vessels={vessels}
        markedVesselNames={markedVesselNames}
        recenterToPoint={recenterToPoint}
        setMarkedVesselNames={setMarkedVesselNames}
        setOpenDrawer={setOpenDrawer}
        setOpenVesselList={setOpenVesselList}
        setSelectedVessel={setSelectedVessel}
        setSelectedVesselAnchor={setSelectedVesselAnchor}
        onMapVesselClick={onMapVesselClick}
      />
    )
  }

  if (reportedVessels.length > 0) {
    addVesselIcon(reportedVessels, "reported")
  }
  if (planVessels.length > 0) {
    addVesselIcon(planVessels, "planned")
  }
  if (!switchReported && autoVessels.length > 0) {
    addVesselIcon(autoVessels, "auto-real")
  }
  if (lastPositionVessels.length > 0) {
    addVesselIcon(lastPositionVessels, "last-position")
  }

  // const end = Date.now()
  // console.log(`VesselController took: ${end - start} ms`)
  return <>{layers}</>
}
